/** Single source of truth for separating trip *shape* from stopover *attribute*.
 *
 * The tracker currently collapses both into `itinerary_shape` ('oneway','return','stopover').
 * This module exposes the two genuinely independent dimensions so that a one-way stopover
 * is grouped, filtered, and labelled correctly the moment the upstream feed can produce one.
 *
 * CHO-630 introduced an `inbound_date != null` heuristic to decide whether a row with
 * `itinerary_shape === 'stopover'` is really a round trip. That heuristic is confined to
 * `tripShape()` below; the companion tracker ticket in serenterprise-ai-a/flight-tracker
 * will add an honest shape/stopover split column and remove this branch.
 */

export type TripShape = 'oneway' | 'return';

export interface ObservationLike {
  origin: string;
  destination: string;
  outbound_date: string;
  inbound_date: string | null;
  itinerary_shape: string;
  stopover_point: string | null;
  stopover_leg: string | null;
  stopover_nights: number | null;
  carrier: string;
}

/** Whether the itinerary includes a stopover. Attribute, not shape. */
export function hasStopover(row: Pick<ObservationLike, 'stopover_point'>): boolean {
  return row.stopover_point != null;
}

/** The trip's shape, independent of stopover-ness. */
export function tripShape(row: Pick<ObservationLike, 'itinerary_shape' | 'inbound_date'>): TripShape {
  if (row.itinerary_shape === 'oneway') return 'oneway';
  if (row.itinerary_shape === 'return') return 'return';
  if (row.itinerary_shape === 'stopover') {
    // CHO-630 heuristic: the tracker currently stores round-trip AND one-way stopovers
    // under the collapsed 'stopover' value. Once serenterprise-ai-a/flight-tracker adds
    // an honest shape column, read that field here and delete this fallback branch.
    return row.inbound_date != null ? 'return' : 'oneway';
  }
  // Unknown shape: fail soft, never throw in a render path.
  return row.inbound_date != null ? 'return' : 'oneway';
}
